import { useCallback, useEffect, useState } from 'react';
import If from '../../../../Atoms/Conditionals/If';
import Mailer from '../../../../paytax-calculator/mailer';
import { componentMap, transformToEmailForm } from '../../../DescriptionComp';
import { getDepreciationTable } from '../../formulas/depreciation-formula/depreciation-table';
import { getYearWiseData } from '../../formulas/emi-formula/yearWiseData';
import { getLTCGTable } from '../../formulas/ltcg-formula/ltcg-table';
import { getNetworthTable } from '../../formulas/networth-formula/nw-table';
import { getSalaryTable } from '../../formulas/salary-formula/salarytake-table';
import {
  IAccordionField,
  ICALCULATOR,
  IField,
  IGraphField,
  IOutputField,
  ISelectField,
  TCALCULATOR_FIELD_TYPE,
} from '../../types';
import {
  RenderCalculatorComponent,
  getDefaultValues,
  getFieldsByType,
  getFormula,
  getOrderedFields,
  getToggleVisibilityFields,
} from '../../utils';
import { getCalculatorTheme } from '../../utils/theme-detector';
import { YearWiseAccordion } from '../YearWiseAccordion';

interface ICALC {
  calcData: ICALCULATOR;
}

export const Calculator = ({ calcData }: ICALC) => {
  const {
    formula = '',
    title = '',
    sendMail = '',
    calculatorDescription = '',
    inputLayout = '',
  } = calcData;
  const sortedFields = getOrderedFields(calcData.fields);
  const [fields, setFields] = useState<TCALCULATOR_FIELD_TYPE>(sortedFields);

  // Get calculator theme configuration
  const theme = getCalculatorTheme(sortedFields);

  // Theme-based initial data configuration
  const initialInputData = getDefaultValues(fields, 'input');
  const initialOutputData = theme.behavior.includeOutputInFormData
    ? getDefaultValues(fields, 'output')
    : {};
  const initialFormData = { ...initialInputData, ...initialOutputData };
  const [formData, setFormData] = useState(initialFormData);
  const [calculatedResult, setCalculatedResult] = useState({});
  const emailResult = transformToEmailForm(title, calculatedResult, formData);
  const DescriptionComponent = calculatorDescription
    ? componentMap[calculatorDescription as string]
    : null;

  useEffect(() => {
    const result = getFormula(formula, formData);
    setCalculatedResult(result);
  }, [formData, formula]);

  const updateMonthlyHide = useCallback(
    (key: keyof TCALCULATOR_FIELD_TYPE, hidden: boolean) => {
      setFields((prevData) => ({
        ...prevData,
        [key]: {
          ...(prevData[key] as
            | IField
            | IOutputField
            | IGraphField
            | IAccordionField
            | ISelectField),
          hide: hidden,
        },
      }));
    },
    []
  );

  const handleInputChange = useCallback(
    (fieldName: string, value: string | number) => {
      const formDataUpdates: Record<string, string | number> = {};

      Object.entries(toggleVisibilityFields).forEach(
        ([fieldKey, fieldData]) => {
          if (fieldName !== fieldKey || !('options' in fieldData)) return;

          // 1. Collect all dependent fields from all options
          const allSubFieldIds =
            fieldData?.options?.flatMap((option) => option?.subFieldId || []) ||
            [];

          // 2. Hide everything first (deterministic reset)
          allSubFieldIds?.forEach((subFieldId) => {
            if (subFieldId) updateMonthlyHide(subFieldId, true);
          });

          // 3. Checkbox handling (supports multiple options)
          if (fieldData?.type === 'checkbox') {
            fieldData?.options?.forEach((option) => {
              const shouldShow = value !== 1;

              option?.subFieldId?.forEach((subFieldId) => {
                if (subFieldId) {
                  updateMonthlyHide(subFieldId, !shouldShow);
                }
              });
            });
            return;
          }

          // 4. Radio / dropdown / toggle handling
          const selectedOption = fieldData?.options?.find(
            (option) => option?.value === value
          );

          if (selectedOption?.subFieldId) {
            selectedOption?.subFieldId?.forEach((subFieldId) => {
              if (subFieldId) updateMonthlyHide(subFieldId, false);
            });
          }
        }
      );

      formDataUpdates[fieldName] = value;

      setFormData((prevFormData) => ({
        ...prevFormData,
        ...formDataUpdates,
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateMonthlyHide, setFormData]
  );

  if (!calcData || !fields) return null;

  const inputFields = getFieldsByType(fields, 'input');
  const contentFields = getFieldsByType(fields, 'content');
  const outputFields = getFieldsByType(fields, 'output');
  const graphFields = getFieldsByType(fields, 'graph');
  const accordionFields = getFieldsByType(fields, 'accordion');
  const toggleVisibilityFields = getToggleVisibilityFields(fields);
  const yearDropdown = accordionFields.yearDropdown as
    | IAccordionField
    | undefined;
  const table = accordionFields.table as IAccordionField | undefined;

  if (yearDropdown) {
    const yearWiseData = yearDropdown.data[0];
    const dataaa = getYearWiseData({
      ...formData,
      ...calculatedResult,
      headers: yearWiseData?.headers,
    });

    //@ts-ignore
    if (yearWiseData) yearWiseData.data = dataaa;
  }

  //TODO: Need to refactor. Create a util file to get table function
  if (table?.title === 'salary') {
    const t = table.data[0];
    const dat = getSalaryTable({
      ...formData,
      ...calculatedResult,
      headers: t?.headers,
    });
    // @ts-ignore
    if (t) t.data = dat;
  } else if (table?.title === 'networth') {
    const t = table.data[0];
    const dat = getNetworthTable({
      ...formData,
      ...calculatedResult,
      headers: t?.headers,
    });
    // @ts-ignore
    if (t) t.data = dat;
  } else if (table?.title === 'depreciation') {
    const t = table.data[0];
    const dat = getDepreciationTable({
      ...formData,
      ...calculatedResult,
      headers: t?.headers,
    });
    // @ts-ignore
    if (t) t.data = dat;
  } else if (table?.title === 'ltcg') {
    const t = table.data[0];
    const dat = getLTCGTable({
      ...formData,
      ...calculatedResult,
      headers: t?.headers,
    });
    // @ts-ignore
    if (t) t.data = dat;
  }
  return (
    <div data-testid="calculator-section">
    
      <div
        className="bg-white rounded-xl overflow-hidden"
        style={{
          boxShadow: theme.styling.boxShadow,
        }}
      >
        <div className={theme.styling.container}>
          {/* Input Section */}
          <div className="w-7/12 sm:w-full">
            <div
              className={`${theme.styling.inputSection}`}
            >
              <div
                className={
                  inputLayout === 'grid'
                    ? 'grid grid-cols-2 gap-6 sm:grid-cols-1 sm:gap-4'
                    : ''
                }
              >
                {Object.entries(inputFields)?.map(([fieldKey, fieldData]) => (
                  <div key={fieldKey}>
                    <RenderCalculatorComponent
                      title={title}
                      fieldKey={fieldKey}
                      fieldData={fieldData}
                      handleInputChange={handleInputChange}
                      formData={formData}
                      calculatedResult={calculatedResult}
                      calculatorFields={sortedFields}
                    />
                  </div>
                ))}
              </div>
            </div>
            {Object.keys(contentFields).length > 0 && (
              <div className="px-12 pb-6 sm:px-6">
                {Object.entries(contentFields)?.map(([fieldKey, fieldData]) => (
                  <div key={fieldKey}>
                    <RenderCalculatorComponent
                      fieldKey={fieldKey}
                      fieldData={fieldData}
                      handleInputChange={handleInputChange}
                      formData={formData}
                      calculatedResult={calculatedResult}
                      calculatorFields={sortedFields}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Output Section */}
          <div className="w-5/12 relative sm:w-full border-l border-gray-100 sm:border-l-0 sm:border-t">
            <div
              className={`${theme.styling.outputSection}`}
            >
              {Object.entries(graphFields)?.map(([fieldKey, fieldData]) => (
                <div key={fieldKey}>
                  <RenderCalculatorComponent
                    fieldKey={fieldKey}
                    fieldData={fieldData}
                    handleInputChange={handleInputChange}
                    formData={formData}
                    calculatedResult={calculatedResult}
                    calculatorFields={sortedFields}
                  />
                </div>
              ))}
              {Object.entries(outputFields)?.map(([fieldKey, fieldData]) => (
                <div key={fieldKey}>
                  <RenderCalculatorComponent
                    fieldKey={fieldKey}
                    fieldData={fieldData}
                    handleInputChange={handleInputChange}
                    formData={formData}
                    calculatedResult={calculatedResult}
                    calculatorFields={sortedFields}
                  />
                </div>
              ))}
            </div>
            <If condition={!!sendMail}>
              <div
                className={`hidden p-6 relative w-full sm:p-6 xs:p-2 border-t border-solid border-grey-250`}
              >
                <Mailer
                  emailData={emailResult}
                  slug="SendHraCalculatorEmail"
                  buttonclass="w-[100px] rounded-md"
                />
              </div>
            </If>
          </div>
        </div>
      </div>
      {/* Accordion Section - Outside unified container */}
      {theme.behavior.showAccordion && (
        <div className="bg-white shadow-lg my-6 rounded-md sm:hidden">
          {Object.entries(accordionFields)?.map(
            ([fieldKey, fieldData], index) => {
              if ('data' in fieldData && 'isYearDropdown' in fieldData) {
                return (
                  <YearWiseAccordion
                    key={`${fieldKey}_${index}`}
                    section={fieldData.section}
                    type={fieldData.type}
                    data={fieldData.data}
                  />
                );
              }
              return null;
            }
          )}
        </div>
      )}
      {theme.behavior.showConditionalSpacing &&
        formData.isSameState === 'no' && <div className="mb-5"></div>}
      <div className="mt-12 mb-6 font-medium text-base text-[#4B5159] sm:mt-6">
        {DescriptionComponent ? <DescriptionComponent /> : <></>}
      </div>
      <div className="bg-white shadow-lg rounded-md hidden sm:block sm:mt-9">
        {Object.entries(accordionFields)?.map(
          ([fieldKey, fieldData], index) => {
            if ('data' in fieldData && 'isYearDropdown' in fieldData) {
              return (
                <YearWiseAccordion
                  key={`${fieldKey}_${index}`}
                  section={fieldData.section}
                  type={fieldData.type}
                  data={fieldData.data}
                />
              );
            }
            return null;
          }
        )}
      </div>
    </div>
  );
};
